import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import { environment } from '@environments/environment';
import { AccountStore } from '../stores/account-store';
import { AuthStore } from '../stores/auth-store';
import { Storage } from '../services/storage';
import type {
  LinkedMerchantsRequest,
  LinkedMerchantsResponse,
  MerchantDetailsResponse,
} from '../models';

/**
 * MerchantApi - API service for merchant-related endpoints
 *
 * Handles all merchant-specific API calls including:
 * - Get merchant details
 * - Fetch merchant balance
 * - List the merchants the signed-in merchant user is linked to
 */
@Injectable({
  providedIn: 'root',
})
export class MerchantApi {
  private readonly http = inject(HttpClient);
  private readonly authStore = inject(AuthStore);
  private readonly accountStore = inject(AccountStore);
  private readonly storage = inject(Storage);

  /**
   * Get merchant account details.
   *
   * Endpoint: `POST /api/merchant/details_v1`
   *
   * ⚠️ **Behaviour change — verify against staging.** This previously sent the *merchant*
   * id in the `account_id` field, because the caller passed `account._id` and that field is
   * the merchant id in disguise. It worked only through the legacy fallback documented for
   * solo merchants ("falls back to the Merchant `_id` itself"), which does not apply to a
   * genuine multi-merchant user.
   *
   * It now sends the true `MerchantAccountUser` id in `account_id` — consistent with every
   * other endpoint in the app, all of which already do this — and the account's own
   * `merchant_id` alongside it, resolved from the account row rather than from the session
   * so it tracks the selected merchant instead of the one signed in with.
   *
   * If staging disagrees, the one-line revert is `account_id: merchantId`.
   *
   * @param accountId - `MerchantAccount.accountId`
   */
  getMerchantDetails(accountId: string): Observable<MerchantDetailsResponse> {
    const token = this.authStore.getToken();
    const account = this.accountStore.getAccountById(accountId);
    const merchantId = account?.merchantId || this.authStore.getMerchantId() || '';

    return this.http
      .post<MerchantDetailsResponse>(`${environment.apiUrl}/api/merchant/details_v1`, {
        account_id: accountId,
        device_type: environment.deviceType,
        merchant_id: merchantId,
        token: token || '',
        user_type: 'merchant',
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error('❌ Failed to get merchant details:', error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * List every merchant the signed-in merchant user is linked to, with each one's `logo_url`
   * and `image_url`.
   *
   * Endpoint: `POST /api/merchant/get_linked_merchant`
   *
   * One aggregate covers the whole account switcher, which is why this exists: reading the same
   * logos through `details_v1` costs one heavy call per account (delivery-fee computation,
   * country/city lookups, operating hours) to return one string each. The Android merchant app
   * uses this same endpoint for the same list.
   *
   * Credentials come from `Storage` rather than `AuthStore` because `merchant_user_id` — the
   * only field the endpoint actually reads — is not on `AuthStore`'s public surface. Same body
   * shape as `WalletHomeApi.getWalletHome`.
   *
   * Rows are keyed by **merchant** id (`_id`), not account id — see {@link LinkedMerchantRow}.
   * An unlinked or solo merchant legitimately gets `data: []`.
   */
  getLinkedMerchants(): Observable<LinkedMerchantsResponse> {
    const userData = this.storage.getUserData();
    if (!userData) {
      return throwError(() => new Error('User data not found in storage'));
    }

    const payload: LinkedMerchantsRequest = {
      merchant_id: userData.merchantId,
      account_id: this.accountStore.resolveAccountId(),
      merchant_user_id: userData.merchantUserId,
      user_type: 'merchant',
      device_type: environment.deviceType,
      token: userData.token,
    };

    return this.http
      .post<LinkedMerchantsResponse>(
        `${environment.apiUrl}/api/merchant/get_linked_merchant`,
        payload,
      )
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  /**
   * Refresh merchant balance only
   * Can be used for quick balance updates without fetching all merchant data
   *
   * @param accountId - Merchant account ID
   */
  refreshBalance(accountId: string): Observable<MerchantDetailsResponse> {
    return this.getMerchantDetails(accountId);
  }
}
